import { useSearchParams, Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { SearchX } from "lucide-react";
import { api } from "@/lib/api";
import { CardGrid, CardGridSkeleton } from "@/components/cards/CardGrid";
import { LibraryFilters } from "@/components/cards/LibraryFilters";
import { EmptyState, PageHeader, Pager } from "@/components/common/PageParts";

const KEYS = ["q", "colors", "color_mode", "colorless", "type", "rarity", "layout", "set_id", "mv_min", "mv_max", "sort", "page", "creator"];

export default function Library() {
  const [search, setSearch] = useSearchParams();
  const params = Object.fromEntries(KEYS.map((k) => [k, search.get(k) || ""]));
  const page = Number(params.page) || 1;

  const setParam = (key, value) => {
    const next = new URLSearchParams(search);
    value ? next.set(key, value) : next.delete(key);
    if (key !== "page") next.delete("page");
    setSearch(next, { replace: true });
  };

  const query = Object.fromEntries(Object.entries({ ...params, page, page_size: 30 }).filter(([, v]) => v !== "" && v != null));
  const { data, isLoading, isError } = useQuery({
    queryKey: ["cards", query],
    queryFn: () => api.get("/cards", { params: query }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
  const { data: sets } = useQuery({ queryKey: ["sets", "public"], queryFn: () => api.get("/sets", { params: { page_size: 100 } }).then((r) => r.data.items) });

  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Custom cards only" title="Custom Cards" testid="library-header"
        description="Every public fan-made card in the Foundry. Official Magic cards live in the separate, Scryfall-powered Official Cards section."
        actions={
          <>
          <Link to="/upload/planar" className="btn-outline" data-testid="library-upload-planar-link">Upload a Planechase card</Link>
          <select className="field w-auto" value={params.sort || "newest"} onChange={(e) => setParam("sort", e.target.value)} aria-label="Sort cards" data-testid="library-sort-select">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A–Z</option>
            <option value="mana_value">Mana value</option>
          </select>
          </>
        } />
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-3">
          <LibraryFilters params={params} setParam={setParam} reset={() => setSearch({}, { replace: true })} sets={sets} />
        </div>
        <div className="lg:col-span-9">
          <p className="mb-5 font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="library-result-count">
            {data ? `${data.total} card${data.total === 1 ? "" : "s"}` : "Searching…"}
            {params.creator && ` by @${params.creator}`}
          </p>
          {isLoading ? <CardGridSkeleton /> : isError ? (
            <EmptyState icon={SearchX} title="Search failed" testid="library-error">Please try again.</EmptyState>
          ) : data.items.length ? (
            <>
              <CardGrid cards={data.items} testid="library-grid" />
              <Pager page={page} pageSize={30} total={data.total} onChange={(p) => setParam("page", String(p))} testid="library-pager" />
            </>
          ) : (
            <EmptyState icon={SearchX} title="No cards match these filters" testid="library-empty">Try widening your search.</EmptyState>
          )}
        </div>
      </div>
    </div>
  );
}
